const BRAND_CONTEXTS = {
  fashion: 'Fashion',
  tech: 'Tech',
  home: 'Home',
  beauty: 'Beauty',
  'kids-baby': 'Kids & Baby',
  'sport-leisure': 'Sport & Leisure',
  'sports-outdoors': 'Sport & Leisure'
};

const BRAND_FALLBACK_COLLECTIONS = {
  Fashion: 'fashion',
  Tech: 'electronics',
  Home: 'home',
  Beauty: 'health-beauty',
  'Kids & Baby': 'kids-baby',
  'Sport & Leisure': 'sport-leisure'
};

const T = (k) => (window.KurlectiveI18n || {})[k] || '';
const CATEGORY_KEYS = {
  Fashion: 'cat_fashion',
  Tech: 'cat_tech',
  Home: 'cat_home',
  Beauty: 'cat_beauty',
  'Kids & Baby': 'cat_kids_baby',
  'Sport & Leisure': 'cat_sport_leisure'
};
const categoryLabel = (category) => T(CATEGORY_KEYS[category]) || category;
const countLabel = (count, oneKey, otherKey, fallbackOne, fallbackOther) => {
  const template = count === 1 ? (T(oneKey) || fallbackOne) : (T(otherKey) || fallbackOther);
  return template.replace('[count]', String(count));
};

const parseBrandIndex = (hub) => {
  const source = hub.querySelector('[data-brand-index-json]');
  if (!source) return [];
  try {
    let value = JSON.parse(source.textContent || '[]');
    for (let depth = 0; depth < 3 && typeof value === 'string'; depth += 1) value = JSON.parse(value);
    if (!Array.isArray(value)) value = value?.brands || value?.value || [];
    if (!Array.isArray(value)) return [];
    return value
      .filter((brand) => brand && typeof brand === 'object' && (brand.name || brand.key || brand.slug))
      .map((brand) => ({
        name: String(brand.name || brand.key || brand.slug || '').trim(),
        key: String(brand.key || brand.slug || '').trim(),
        slug: String(brand.slug || brand.key || '').trim(),
        categories: Array.isArray(brand.categories) ? brand.categories.filter(Boolean).map(String) : [],
        count: Number.isFinite(Number(brand.count)) ? Number(brand.count) : 0
      }))
      .filter((brand) => brand.name);
  } catch (error) {
    console.warn('Kurlective brand index could not be parsed.', error);
    return [];
  }
};

const brandLetter = (name) => {
  const first = (name || '').trim().charAt(0).toLocaleUpperCase();
  return /^[A-Z]$/.test(first) ? first : '0-9';
};

const buildBrandSearchUrl = (hub, brandName) => {
  const root = window.Shopify?.routes?.root || '/';
  const base = hub.dataset.brandSearchUrl || (root + 'search');
  const url = new URL(base, window.location.origin);
  url.searchParams.set('q', brandName);
  url.searchParams.set('type', 'product');
  return url.pathname + url.search;
};

const createBrandRow = (hub, brand) => {
  const row = document.createElement('a');
  row.className = 'store-directory-row brand-directory-row';
  row.href = buildBrandSearchUrl(hub, brand.name);
  row.dataset.brandRow = '';
  row.dataset.brandName = brand.name;
  row.dataset.brandKey = brand.key;
  row.dataset.brandLetter = brandLetter(brand.name);
  row.dataset.brandCategories = brand.categories.join('|');

  const name = document.createElement('strong');
  name.textContent = brand.name;

  const meta = document.createElement('span');
  if (brand.count > 0) {
    meta.textContent = countLabel(
      brand.count,
      'brand_indexed_products_one',
      'brand_indexed_products_other',
      '[count] indexed product',
      '[count] indexed products'
    );
  } else if (brand.categories.length) {
    meta.textContent = brand.categories.map(categoryLabel).join(' · ');
  } else {
    meta.textContent = T('brand_label') || 'Brand';
  }

  const arrow = document.createElement('b');
  arrow.textContent = '↗';

  row.append(name, meta, arrow);
  return row;
};

const renderBrandDirectory = (hub, brands) => {
  const host = hub.querySelector('[data-brand-groups]');
  if (!host) return;

  const grouped = new Map([['0-9', []]]);
  'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').forEach((letter) => grouped.set(letter, []));
  brands.forEach((brand) => grouped.get(brandLetter(brand.name)).push(brand));

  const fragment = document.createDocumentFragment();
  grouped.forEach((items, letter) => {
    if (!items.length) return;

    const section = document.createElement('section');
    section.className = 'store-letter-group';
    section.dataset.brandGroup = letter;

    const heading = document.createElement('div');
    heading.className = 'store-letter-heading';
    heading.textContent = letter;

    const list = document.createElement('div');
    list.className = 'store-directory-list';

    items.forEach((brand) => list.append(createBrandRow(hub, brand)));
    section.append(heading, list);
    fragment.append(section);
  });

  host.replaceChildren(fragment);
};

const updateCategoryCards = (hub, brands) => {
  const cards = Array.from(hub.querySelectorAll('[data-brand-category-filter]'));
  cards.forEach((card) => {
    const category = card.dataset.brandCategoryFilter;
    const count = brands.reduce((total, brand) => total + (brand.categories.includes(category) ? 1 : 0), 0);
    const target = card.querySelector('[data-brand-category-count]');
    if (!brands.length) {
      // The Shopify market brand index is temporarily unavailable. Keep category
      // choices usable by routing to their existing L1 collections, not an empty directory.
      const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const handle = BRAND_FALLBACK_COLLECTIONS[category];
      card.hidden = !handle;
      if (handle) card.href = root + 'collections/' + handle;
      if (target) target.textContent = hub.dataset.brandFallbackLabel || 'Explore category';
      return;
    }
    card.hidden = count === 0;
    if (target) {
      target.textContent = countLabel(count, 'brand_count_one', 'brand_count_other', '[count] brand', '[count] brands');
    }
  });
};

const initBrandHub = (hub) => {
  if (hub.dataset.brandHubReady === 'true') return;
  hub.dataset.brandHubReady = 'true';

  const brands = parseBrandIndex(hub);
  const registryCount = hub.querySelector('[data-brand-registry-count]');
  const registryEmpty = hub.querySelector('[data-brand-registry-empty]');
  const directory = hub.querySelector('[data-brand-directory]');

  if (registryCount && brands.length) {
    registryCount.textContent = countLabel(
      brands.length,
      'brand_registry_one',
      'brand_registry_other',
      '[count] brand in the current registry',
      '[count] brands in the current registry'
    );
  }

  updateCategoryCards(hub, brands);
  renderBrandDirectory(hub, brands);

  const chooser = hub.querySelector('[data-brand-category-browser]');
  const goToChooser = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('category');
    url.searchParams.delete('letter');
    if (url.hash === '#brand-directory') url.hash = '';
    history.replaceState(history.state, '', url);
    requestAnimationFrame(() => {
      chooser?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const heading = chooser?.querySelector('h2');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    });
  };
  if (registryEmpty) registryEmpty.hidden = brands.length > 0;
  if (!directory || brands.length === 0) {
    directory?.querySelector('[data-brand-gate]')?.setAttribute('hidden', '');
    hub.querySelectorAll('[data-brand-category-return]').forEach((button) => {
      button.addEventListener('click', goToChooser);
    });
    return;
  }

  const rows = Array.from(directory.querySelectorAll('[data-brand-row]'));
  const groups = Array.from(directory.querySelectorAll('[data-brand-group]'));
  const letterButtons = Array.from(directory.querySelectorAll('[data-brand-letter]'));
  const categoryLinks = Array.from(hub.querySelectorAll('[data-brand-category-filter]:not([hidden])'));
  const gate = directory.querySelector('[data-brand-gate]');
  const content = directory.querySelector('[data-brand-directory-content]');
  const showAll = directory.querySelector('[data-brand-show-all]');
  const clearCategory = directory.querySelector('[data-brand-category-clear]');
  const activeCategoryLabel = directory.querySelector('[data-brand-active-category]');
  const title = directory.querySelector('[data-brand-directory-title]');
  const initialDirectoryTitle = title?.textContent || '';
  const visibleCount = directory.querySelector('[data-brand-visible-count]');
  const input = directory.querySelector('[data-brand-search]');
  const empty = directory.querySelector('[data-brand-empty]');

  let activeCategory = null;
  let activeLetter = 'all';
  let query = '';

  const categoriesFor = (row) => (row.dataset.brandCategories || '').split('|').filter(Boolean);
  const letterFor = (row) => row.dataset.brandLetter || '0-9';
  const matchesCategory = (row) => activeCategory === 'all' || categoriesFor(row).includes(activeCategory);
  const matchesSearch = (row) => !query || (row.dataset.brandName || '').toLocaleLowerCase().includes(query);
  const matchesLetter = (row) => activeLetter === 'all' || letterFor(row) === activeLetter;

  const setHidden = (node, hidden) => {
    if (!node) return;
    node.hidden = hidden;
    node.classList.toggle('is-filtered-out', hidden);
  };

  const updateLetters = () => {
    const available = new Set(rows.filter((row) => matchesCategory(row) && matchesSearch(row)).map(letterFor));
    letterButtons.forEach((button) => {
      const value = button.dataset.brandLetter;
      button.disabled = value !== 'all' && !available.has(value);
      button.classList.toggle('active', value === activeLetter);
    });
    if (activeLetter !== 'all' && !available.has(activeLetter)) activeLetter = 'all';
  };

  const apply = () => {
    if (!activeCategory) return;
    updateLetters();
    let count = 0;
    rows.forEach((row) => {
      const show = matchesCategory(row) && matchesSearch(row) && matchesLetter(row);
      setHidden(row, !show);
      if (show) count += 1;
    });
    groups.forEach((group) => {
      const hasVisible = Array.from(group.querySelectorAll('[data-brand-row]')).some((row) => !row.hidden);
      setHidden(group, !hasVisible);
    });
    if (visibleCount) {
      visibleCount.textContent = countLabel(count, 'brand_count_one', 'brand_count_other', '[count] brand', '[count] brands');
    }
    if (empty) empty.hidden = count > 0;
  };

  const slugFor = (category) => {
    if (category === 'all') return 'all';
    if (category === 'Sport & Leisure') return 'sport-leisure';
    const entry = Object.entries(BRAND_CONTEXTS).find(([, value]) => value === category);
    return entry ? entry[0] : '';
  };

  const setCategory = (category, updateUrl = true, scroll = false) => {
    activeCategory = category;
    activeLetter = 'all';
    query = '';
    if (input) input.value = '';

    categoryLinks.forEach((link) => link.classList.toggle('active', link.dataset.brandCategoryFilter === category));
    if (gate) gate.hidden = true;
    if (content) content.hidden = false;
    if (activeCategoryLabel) activeCategoryLabel.textContent = category === 'all' ? (T('all_brands') || 'All brands') : categoryLabel(category);
    if (title) title.textContent = category === 'all' ? (T('all_brands') || 'All brands') : (T('category_brands') || '[category] brands').replace('[category]', categoryLabel(category));

    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('category', slugFor(category));
      url.searchParams.delete('letter');
      history.replaceState({}, '', url);
    }

    apply();
    if (scroll) requestAnimationFrame(() => directory.scrollIntoView({ behavior:'smooth', block:'start' }));
  };

  categoryLinks.forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      setCategory(link.dataset.brandCategoryFilter, true, true);
    });
  });

  showAll?.addEventListener('click', () => setCategory('all', true, false));

  const resetToChooser = () => {
    activeCategory = null;
    activeLetter = 'all';
    query = '';
    if (input) input.value = '';
    categoryLinks.forEach((link) => {
      link.classList.remove('active');
      link.removeAttribute('aria-current');
    });
    letterButtons.forEach((button) => {
      button.classList.toggle('active', button.dataset.brandLetter === 'all');
      button.disabled = false;
    });
    rows.forEach((row) => setHidden(row, false));
    groups.forEach((group) => setHidden(group, false));
    if (empty) empty.hidden = true;
    if (activeCategoryLabel) activeCategoryLabel.textContent = '';
    if (title) title.textContent = initialDirectoryTitle;
    if (content) content.hidden = true;
    if (gate) gate.hidden = false;
    if (visibleCount) visibleCount.textContent = '';
    goToChooser();
  };
  clearCategory?.addEventListener('click', resetToChooser);
  hub.querySelectorAll('[data-brand-category-return]').forEach((button) => {
    button.addEventListener('click', resetToChooser);
  });

  input?.addEventListener('input', () => {
    query = input.value.trim().toLocaleLowerCase();
    activeLetter = 'all';
    apply();
  });

  letterButtons.forEach((button) => {
    button.addEventListener('click', () => {
      if (button.disabled) return;
      activeLetter = button.dataset.brandLetter;
      apply();
    });
  });

  const urlParams = new URLSearchParams(window.location.search);
  const requested = urlParams.get('category');
  const requestedLetter = (urlParams.get('letter') || '').toUpperCase();
  const hasLetter = /^[A-Z]$/.test(requestedLetter);
  if (requested === 'all' || (!requested && hasLetter)) {
    setCategory('all', false, false);
    if (hasLetter && letterButtons.some(button => button.dataset.brandLetter === requestedLetter && !button.disabled)) {
      activeLetter = requestedLetter;
      apply();
    }
    requestAnimationFrame(() => directory.scrollIntoView({ behavior:'auto', block:'start' }));
  } else if (requested && BRAND_CONTEXTS[requested]) {
    setCategory(BRAND_CONTEXTS[requested], false, false);
    requestAnimationFrame(() => directory.scrollIntoView({ behavior:'auto', block:'start' }));
  }
};

document.querySelectorAll('[data-brand-hub]').forEach(initBrandHub);
